import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Territory } from '../../types/dashboard';
import type { AopRow } from '../../hooks/useEntityData';
import { Panel } from '../ui/Panel';
import { StatusBadge } from '../ui/StatusBadge';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton, ToggleSwitch } from '../ui/TableControls';
import { EmptyState } from '../ui/EmptyState';
import { useSort } from '../../hooks/useSort';
import { fmtPct } from '../../utils/format';
import { formatChange, formatValue, isCountMetric, metricMeta, type TrendMetric } from '../../utils/metricConfig';
import { AOP_PALETTE } from '../../utils/theme';
import { downloadCsv } from '../../utils/csv';
type Key = 'name' | 'value' | 'vsTerritory' | 'vsNetwork' | 'mom' | 'yoy' | 'retention';
interface AopTableProps {
  territory: Territory;
  rows: AopRow[];
  metric: TrendMetric;
  selectedAopId?: string;
  networkAvg: number;
  territoryAvg: number;
}
export function AopTable({
  territory,
  rows,
  metric,
  selectedAopId,
  networkAvg,
  territoryAvg
}: AopTableProps) {
  const navigate = useNavigate();
  const {
    search
  } = useLocation();
  const [belowOnly, setBelowOnly] = useState(false);
  const meta = metricMeta(metric);
  const fmt = (v: number) => formatValue(metric, v);
  const visible = useMemo(() => rows.filter(r => !belowOnly || r.vsNetwork < 0), [rows, belowOnly]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<AopRow, Key>(visible, {
    key: 'vsNetwork',
    dir: 'asc'
  }, (r, k) => {
    if (k === 'name') return r.aop.name;
    if (k === 'value') return r.value;
    return r[k];
  });
  const colorIdx = (id: string) => territory.aops.findIndex(a => a.id === id);
  const changeCell = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-536" data-mp="18yufr11yzhi9">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-537" data-mp="18xt16mlqwcvs">{formatChange(metric, v)}</span>;
  const exportCsv = () => downloadCsv(`i9-aop-${metric}-${territory.id}.csv`, ['AOP', 'Venue', 'ZIP count', meta.label, 'vs Territory Avg', 'vs Network Avg / AOP', 'MoM', 'YoY', 'Season Retention %', 'Status'], sorted.map(r => [r.aop.name, r.aop.venue, r.aop.zipCount, Number(r.value.toFixed(4)), formatChange(metric, r.vsTerritory), formatChange(metric, r.vsNetwork), r.mom === null ? '' : formatChange(metric, r.mom), r.yoy === null ? '' : formatChange(metric, r.yoy), r.retention === null ? '' : (r.retention * 100).toFixed(2), r.status]));
  return <Panel title="AOP performance" definitionId="aop" subtitle={`${meta.label} by Area of Play · territory avg ${fmt(territoryAvg)} · network avg per AOP ${fmt(networkAvg)} · click a row to focus an AOP`} actions={<>
          <ToggleSwitch checked={belowOnly} onChange={setBelowOnly} label="Below network avg" data-id="element-539" data-mp="9enabz2skghb" />
          <ExportButton onClick={exportCsv} data-id="element-540" data-mp="ecfathhi5ax4" />
        </>} bodyClassName="p-0" data-id="element-538" data-mp="2q2re8waity2g">
      {sorted.length === 0 ? <EmptyState title="No AOPs below the network average" description={`Every AOP in ${territory.name} meets or beats the network on ${meta.label.toLowerCase()}.`} data-id="element-541" data-mp="ofkebmnh0by7" /> : <div className="overflow-x-auto" data-id="element-542" data-mp="eqanea4d4zbo">
          <table className="w-full min-w-[860px] border-collapse text-[12.5px]" data-id="element-543" data-mp="eozho4rcxhhx">
            <thead className="bg-navy-50 text-navy" data-id="element-544" data-mp="aoz2ghwfp29t">
              <tr data-id="element-545" data-mp="5qx8v31dtkk3">
                <SortableTh label="AOP" sortKey="name" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-546" data-mp="5rfdoo24173m" />
                <SortableTh label={isCountMetric(metric) ? 'Total' : meta.label} sortKey="value" sort={sort} onSort={onSort} data-id="element-547" data-mp="qjdakgyw32y" />
                <SortableTh label="vs territory avg" sortKey="vsTerritory" sort={sort} onSort={onSort} data-id="element-548" data-mp="3rzordn2nvmdo" />
                <SortableTh label="vs network avg" sortKey="vsNetwork" sort={sort} onSort={onSort} data-id="element-549" data-mp="3nyd962m4a3fi" />
                <SortableTh label="MoM" sortKey="mom" sort={sort} onSort={onSort} data-id="element-550" data-mp="1q3wl4joag98t" />
                <SortableTh label="YoY" sortKey="yoy" sort={sort} onSort={onSort} data-id="element-551" data-mp="1m5fee18s50rx" />
                <SortableTh label="Season ret." sortKey="retention" sort={sort} onSort={onSort} data-id="element-552" data-mp="1t8dhkrgcfzlx" />
                <th scope="col" className="whitespace-nowrap py-2.5 pl-3 pr-5 text-left font-semibold" data-id="element-553" data-mp="1y6ezh9219svt">Status</th>
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-554" data-mp="1o8hd6r3qi65u">
              {sorted.map(r => {
            const go = () => navigate(`/territory/${territory.id}/aop/${r.aop.id}${search}`);
            const selected = r.aop.id === selectedAopId;
            return <tr key={r.aop.id} tabIndex={0} onClick={go} onKeyDown={e => e.key === 'Enter' && go()} aria-current={selected ? 'true' : undefined} className={`cursor-pointer border-b border-line hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none ${selected ? 'bg-accent-soft' : ''}`} data-id="element-555" data-mp="1fa5q2z22tqi5">
                    <td className="py-2.5 pl-5 pr-3" data-id="element-556" data-mp="cuzxlrmju0xo">
                      <div className="flex items-center gap-2" data-id="element-557" data-mp="cxmfak35lwtm">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{
                    background: AOP_PALETTE[colorIdx(r.aop.id) % AOP_PALETTE.length]
                  }} data-id="element-558" data-mp="7xzzq3zus4hn" />
                        <div data-id="element-559" data-mp="hv4ehirdcxof">
                          <div className="font-semibold text-navy" data-id="element-560" data-mp="httabhu0hnws">{r.aop.name}</div>
                          <div className="text-[11px] text-ink-subtle" data-id="element-561" data-mp="vu468za6hsot">{r.aop.venue} · {r.aop.zipCount} ZIPs</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold" data-id="element-562" data-mp="15kyqjy3v6i7m">{fmt(r.value)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-563" data-mp="10lm6tm8ahr99">{changeCell(r.vsTerritory)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-564" data-mp="1el52vgnttc57">{changeCell(r.vsNetwork)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-565" data-mp="1eisnnb9gdfvu">{changeCell(r.mom)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-566" data-mp="19jz697r1p4qe">{changeCell(r.yoy)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-567" data-mp="7a7atq7fymz8">{r.retention === null ? '—' : fmtPct(r.retention)}</td>
                    <td className="py-2.5 pl-3 pr-5" data-id="element-568" data-mp="c5ml6868q0qw"><StatusBadge status={r.status} data-id="element-569" data-mp="g9kjwhrnl9jf" /></td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </Panel>;
}